import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {AnalyticsService} from '../../shared/analytics.service';
import {AuthService} from '../../shared/auth.service';
import {ModalComponent} from '../../shared/modal/modal.component';
import {BehaviorSubject, Observable} from 'rxjs';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {map, startWith} from 'rxjs/operators';
import {ClientApiService} from '../client-api.service';
import {NavigationService} from '../../shared/navigation.service';
import {AppStateService} from '../../shared/app-state.service';
import * as differenceInDays from 'date-fns/difference_in_days';
import * as startOfToday from 'date-fns/start_of_today';
import * as format from 'date-fns/format';

@Component({
  selector: 'update-assignment-modal',
  templateUrl: './update-assignment-modal.component.html',
  styleUrls: ['./update-assignment-modal.component.scss']
})
export class UpdateAssignmentModalComponent implements OnInit {

  @ViewChild('modal') modal: ModalComponent;

  @Input() order: Order;
  @Output() assignmentUpdated = new EventEmitter<void>();

  endDateForm: FormGroup;
  isUpdateDisabled$: Observable<boolean>;

  updated = false;
  isoEndDate = '';
  isoMinDate = format(startOfToday(), 'YYYY-MM-DD');
  brand$ = this.AppState.brand$;
  state$ = new BehaviorSubject('update');
  loading$ = new BehaviorSubject(false);
  isDeferPossible$ = new BehaviorSubject(true);
  masquerading$ = this.Auth.masquerading$;

  constructor(private Auth: AuthService,
              private Analytics: AnalyticsService,
              private Api: ClientApiService,
              private AppState: AppStateService,
              private Form: FormBuilder,
              private Navigation: NavigationService) {
  }

  ngOnInit() {
    if (this.order.endDate) {
      this.isoEndDate = format(this.order.endDate, 'YYYY-MM-DD');
    }

    this.isDeferPossible$.next(this.isOrderEligibleToDefer());
    this.endDateForm = this.Form.group({endDate: [this.isoEndDate, Validators.required]});

    this.isUpdateDisabled$ = this.endDateForm.statusChanges.pipe(
      startWith(true),
      map(() => {
        const endDate = this.endDateForm.get('endDate').value;

        return this.endDateForm.invalid || endDate === this.isoEndDate || endDate < this.isoMinDate;
      })
    );
  }

  isOrderEligibleToDefer(): boolean {
    return differenceInDays(this.order.endDate, startOfToday()) > 7;
  }

  show(state?: string) {
    if (state) {
      this.state$.next(state);
    }

    if (this.isoEndDate) {
      this.endDateForm.patchValue({endDate: this.isoEndDate});
    }

    this.updated = false;
    this.modal.show();
    this.Analytics.trackEvent(`Update Assignment: show modal in ${this.state$.getValue()} state`);
  }

  hide() {
    this.modal.hide();
  }

  onModalHide() {
    this.state$.next('update');

    if (this.updated) {
      this.assignmentUpdated.emit();
    }

    this.Analytics.trackEvent(`Update Assignment: modal closed`);
  }

  yesClicked() {
    this.Analytics.trackEvent(`Update Assignment: yes clicked`);
    this.state$.next('edit');
  }

  noClicked() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.loading$.next(true);

    this.Api.dismissPositionEndDate(this.order.orderId).subscribe(() => {
      this.loading$.next(false);
      this.state$.next('decline');
      this.updated = true;
    }, err => this.Navigation.handleError(err));

    this.Analytics.trackEvent(`Update Assignment: no clicked`);
  }

  remindClicked() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.loading$.next(true);

    this.Api.deferPositionEndDate(this.order.orderId).subscribe(() => {
      this.loading$.next(false);
      this.state$.next('remind');
      this.updated = true;
    }, err => this.Navigation.handleError(err));

    this.Analytics.trackEvent(`Update Assignment: remind me later clicked`);
  }

  backClicked() {
    this.Analytics.trackEvent(`Update Assignment: back clicked`);

    if (this.state$.getValue() === 'proactive') {
      this.modal.hide();
    } else {
      this.state$.next('update');
    }
  }

  updateEndDate() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.loading$.next(true);

    this.Api.updatePositionEndDate(this.order.orderId, this.endDateForm.get('endDate').value).subscribe(() => {
      this.loading$.next(false);
      this.state$.next('success');
      this.updated = true;
    }, err => this.Navigation.handleError(err));

    this.Analytics.trackEvent(`Update Assignment: end date updated`);
  }

}
